import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthService } from '../lib/services/AuthService';
import { getOnboardingComplete } from '../lib/services/onboardingService';

const LOADING_SAFETY_MS = 8_000;

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [checkingTerms, setCheckingTerms] = useState(true);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const showSpinner = (loading || checkingTerms || checkingOnboarding) && !loadingTimeout;

  useEffect(() => {
    const t = setTimeout(() => setLoadingTimeout(true), LOADING_SAFETY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const checkTermsAcceptance = async () => {
      if (!user) {
        setCheckingTerms(false);
        return;
      }
      if (location.pathname === '/accept-terms') {
        setCheckingTerms(false);
        return;
      }
      try {
        const accepted = await AuthService.hasAcceptedTerms(user.id);
        setHasAcceptedTerms(accepted);
      } catch (error) {
        console.error('Erreur vérification conditions:', error);
        setHasAcceptedTerms(false);
      } finally {
        setCheckingTerms(false);
      }
    };

    if (!loading && user) {
      checkTermsAcceptance();
    } else if (!loading && !user) {
      setCheckingTerms(false);
      setCheckingOnboarding(false);
    }
  }, [user, loading, location.pathname]);

  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user) {
        setCheckingOnboarding(false);
        return;
      }
      try {
        const { complete } = await getOnboardingComplete();
        setOnboardingComplete(complete);
      } catch (error) {
        console.error('Erreur vérification onboarding:', error);
        setOnboardingComplete(true);
      } finally {
        setCheckingOnboarding(false);
      }
    };

    if (!loading && !checkingTerms && user && hasAcceptedTerms) {
      checkOnboarding();
    } else if (!loading && (!user || !hasAcceptedTerms)) {
      setCheckingOnboarding(false);
    }
  }, [user, loading, checkingTerms, hasAcceptedTerms]);

  if (showSpinner) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="text-white mt-4">Chargement...</p>
          {loadingTimeout && (
            <p className="text-dark-400 text-sm mt-2">Connexion lente — redirection vers la page de connexion.</p>
          )}
        </div>
      </div>
    );
  }

  if (loadingTimeout && !user) {
    return <Navigate to="/login" replace />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Si les conditions n'ont pas été acceptées, rediriger vers la page d'acceptation
  if (!hasAcceptedTerms && location.pathname !== '/accept-terms') {
    return <Navigate to="/accept-terms" replace />;
  }

  if (hasAcceptedTerms && !onboardingComplete) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
